import { byIdsResultJSONZ } from "_shared/_api/by-ids/by-ids-result";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import ProposalSchema from "work/proposals/_schemas/proposal-schema";
import z from "zod";

const PROPOSAL_COMPARISONS_URL = "work/proposal-comparisons";

/** Every proposal of one request group: each one the reader may read, and each other one named. */
export const ProposalComparisonJSONZ = byIdsResultJSONZ(ProposalSchema.ProposalJSONZ);
export type ProposalComparisonJSON = z.infer<typeof ProposalComparisonJSONZ>;

/** How many proposals a request group holds, for a group the reader may compare. */
export const ProposalComparisonSummaryJSONZ = z.object({
  id: z.string(),
  proposalCount: z.number().int().nonnegative(),
});
export type ProposalComparisonSummaryJSON = z.infer<typeof ProposalComparisonSummaryJSONZ>;

export const ProposalComparisonSummariesJSONZ = byIdsResultJSONZ(ProposalComparisonSummaryJSONZ);
export type ProposalComparisonSummariesJSON = z.infer<typeof ProposalComparisonSummariesJSONZ>;

/**
 * The client's side-by-side read of the proposals one request produced (#581).
 *
 * A request group is every proposal that shares an `RFPId`. Only the client side may compare one:
 * the server answers a vendor, a reader from another entity, or a reader who may read none of the
 * group with 403, and an id that names no group with 404. A proposal of the group this reader may
 * not read comes back in `unavailable`, never left out.
 */
export const proposalComparisonsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getProposalComparison: build.query<ProposalComparisonJSON, { rfpId: string }>({
      query: ({ rfpId }) => `${PROPOSAL_COMPARISONS_URL}/${rfpId}`,
      rawResponseSchema: ProposalComparisonJSONZ,
      onSchemaFailure: logSchemaFailure,
      // Each proposal's own tag, so a change to any bid, and a bid that becomes readable, refreshes
      // the comparison.
      providesTags: (result) => [
        ...(result?.found ?? []).map((proposal) => ({ type: "Proposal" as const, id: proposal.id ?? undefined })),
        ...(result?.unavailable ?? []).map((entry) => ({ type: "Proposal" as const, id: entry.id })),
        { type: "Proposal" as const, id: "LIST" },
      ],
    }),
    /**
     * The size of each group the reader may compare, for the proposals page to offer "Compare" on a
     * row whose group holds more than one proposal. One request for a page, not one per row.
     */
    getProposalComparisonSummaries: build.query<ProposalComparisonSummariesJSON, { rfpIds: string[] }>({
      query: ({ rfpIds }) => ({
        url: `${PROPOSAL_COMPARISONS_URL}/get-by-ids`,
        method: "POST",
        body: rfpIds,
      }),
      rawResponseSchema: ProposalComparisonSummariesJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: [{ type: "Proposal" as const, id: "LIST" }],
    }),
  }),
});

export const { useGetProposalComparisonQuery, useGetProposalComparisonSummariesQuery } = proposalComparisonsApi;
